import { useTranslation } from "@privacy/translations";
import type { FC } from "react";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { DynamicGrid } from "../../dynamicGrid";
import { DynamicShared } from "../../dynamicShared";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { requiredColDef } from "../../dynamicGrid/misc";
import { EmptyState } from "../../shared/EmptyState";
import { PageTitle } from "../../shared/Page";
import { useGetRisksGridView, useGetRiskForm, prefetchRisksGridView, prefetchRiskForm } from "../../api/risks";
import type { ConnectedDpia, GridViewRisk } from "../../api/risks.types";
import { RiskIcon2, DpiaIcon3 } from "../../shared/icons";
import { measuresQueryOptions } from "../../api/measures";
import { RiskLevelLabel } from "../DpiaGridPage/RiskAssessment";
import { RisksInlineDrawer } from "./RisksDrawer";
import { useQuery } from "@tanstack/react-query";

const RisksGridPage = () => {
  usePrivacyDocumentTitle("Sidebar.Risks");
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const riskGridView = useGetRisksGridView();
  const riskForm = useGetRiskForm();
  const measuresQueryResult = useQuery(measuresQueryOptions());

  return (
    <DynamicGrid.Grid
      rowsData={riskGridView.data?.riskAssessments}
      form={riskForm.data}
      asyncQuestionCardData={{ measuresQueryData: measuresQueryResult.data }}
      getRowId={getRowId}
      mainColumnProps={{
        headerName: t("Risks.Grid.NameHeader"),
        renderLabel: ({ name }) => name,
        renderIcon: ({ iconProps }) => <RiskIcon2 {...iconProps} />,
      }}
      getColumns={questionCardColumns => [
        connectedDpiasColumn({ headerName: t("Risks.Grid.DpiaHeader") }),
        riskLevelColumn({ headerName: t("Risks.Grid.RiskLevelHeader") }),
        descriptionColumn({ headerName: t("Risks.Grid.DescriptionHeader") }),
        ...questionCardColumns,
      ]}
      Toolbar={RisksGridToolbar}
      Footer={RisksGridFooter}
      NoRowsOverlay={NoRisksOverlay}
      texts={{ searchFieldPlaceholder: t("Risks.Grid.SearchFieldLabel") }}
      onRowSelect={selected => navigate(paths.risksInline(selected))}
    />
  );
};

const getRowId = (row: GridViewRisk) => row.id;

const connectedDpiasColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRisk> => ({
  ...requiredColDef({ headerName, field: "connectedDpias" }),
  width: 300,
  valueGetter: (_, row) => row.dpia.name,
  renderCell: ({ row }) => (
    <DynamicGrid.Cell>
      <ConnectedDpiaCell dpia={row.dpia} />
    </DynamicGrid.Cell>
  ),
});

const ConnectedDpiaCell: FC<{ dpia: ConnectedDpia }> = ({ dpia }) => {
  const { paths } = usePrivacyNavigate();
  return (
    <DynamicShared.Link sx={{ mr: 1 }} Icon={DpiaIcon3} key={dpia.id} to={paths.dpiaById(dpia.id)}>
      {dpia.name}
    </DynamicShared.Link>
  );
};

const descriptionColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRisk> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: "description" }),
  width: 280,
  valueGetter: (_, row) => row?.description,
  renderCell: ({ value }) => <DynamicGrid.TextInputCell value={value} />,
});

const riskLevelColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRisk> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: "riskLevel" }),
  width: 280,
  valueGetter: (_, row) => row?.riskLevel?.riskLevelLabel,
  renderCell: ({ row }) => (
    <DynamicGrid.Cell>
      <RiskLevelLabel
        label={row.riskLevel.riskLevelLabel}
        color={row.riskLevel.riskLevelColor}
        backgroundColor={row.riskLevel.riskLevelBackgroundColor}
      />
    </DynamicGrid.Cell>
  ),
});

const RisksGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="Risks.Grid.NumberOfRows" />;

const NoRisksOverlay = () => {
  return <EmptyState labelKey="Risks.Grid.NoRows" />;
};

const RisksGridToolbar: FC = () => {
  const { t } = useTranslation();

  return (
    <DynamicGrid.Toolbar
      pageTitle={<PageTitle title={t("Risks.Title")} subTitle={t("Risks.SubTitle")} />}
      actionButtons={
        <>
          <DynamicGrid.ExportButton />
        </>
      }
    />
  );
};

export const risksGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <RisksGridPage />,
  rowDrawerRoutes: [{ index: true, element: <RisksInlineDrawer /> }],
});

export const prefetchRisksGridViewAndForm = () => {
  prefetchRisksGridView();
  prefetchRiskForm();
};
